import React, { useState, useMemo } from 'react';
import { axiosInstance } from '../../../utils/axios';
import useBoxes from '../../../hooks/useBoxes';
import { SellOrderContext } from '../../../providers/SellOrdersProvider';
import { Box } from '../../../pages/common/interfaces';
import { Container, Header, StyledComponents } from './styles';
import toast from 'react-hot-toast';

type PaymentRequest = {
  id: number;
  date: string;
  amount: string;
  method?: string;
  notes?: string;
  state: 'pending' | 'confirmed' | 'rejected';
  sell_order?: {
    id: number;
    customer?: {
      name: string;
    };
    user?: {
      id: number;
      username: string;
    };
    futureDocument?: string;
    document?: string;
  };
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('es-PE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'UTC'
  });
};

const formatMoney = (amount: number) => {
  return new Intl.NumberFormat('es-PE', {
    style: 'currency',
    currency: 'PEN'
  }).format(amount);
};

const getPaymentIcon = (method?: string) => {
  switch (method?.toUpperCase()) {
    case 'EFECTIVO': return '💰';
    case 'TRANSFERENCIA': return '🏦';
    case 'YAPE': return '💙';
    case 'PLIN': return '💵';
    case 'TARJETA': return '💳';
    default: return '💸';
  }
};

const getVendorOptions = (vendorsArray: any[]) => {
  return [
    <option key="all" value="all">Todos los vendedores</option>,
    ...vendorsArray.map(v => (
      <option key={v.value} value={v.value}>{v.label}</option>
    ))
  ];
};

const getFilteredBoxes = (boxes: Box[], method?: string, futureDocument?: string) => {
  const isBankMethod = method?.toUpperCase() === 'TRANSFERENCIA';
  const isOfficialDocument = futureDocument?.toUpperCase() !== 'PROFORMA';
  return boxes.filter(box =>
    box.isBank === isBankMethod &&
    (isOfficialDocument ? box.isOfficialBox : !box.isOfficialBox)
  );
};

const PendingPaymentsPage: React.FC = () => {
  const { vendorsArray } = React.useContext(SellOrderContext);
  const { boxes } = useBoxes();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PaymentRequest[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVendor, setSelectedVendor] = useState<string>('all');
  const [actionInProgress, setActionInProgress] = useState<{ [key: number]: boolean }>({});
  const [showBoxSelector, setShowBoxSelector] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<PaymentRequest | null>(null);
  const [selectedBox, setSelectedBox] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  // Fetch data
  const fetchData = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/orders/pay-requests/pending');
      setData(res.data);
    } catch (error) {
      console.error('Error fetching pending payments:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter logic: first by vendor, then by search
  const filteredData = useMemo(() => {
    const matchesVendor = (p: PaymentRequest) => {
      if (selectedVendor === 'all') return true;
      const vid = p.sell_order?.user?.id;
      if (vid == null) return false;
      return String(vid) === String(selectedVendor);
    };

    return data.filter(payment => {
      if (!matchesVendor(payment)) return false;
      // Then filter by search term
      if (!searchTerm.trim()) return true;
      const term = searchTerm.toLowerCase();
      return (
        (payment.sell_order?.id?.toString() || '').includes(term) ||
        (payment.sell_order?.customer?.name || '').toLowerCase().includes(term) ||
        (payment.sell_order?.document || '').toLowerCase().includes(term)
      );
    });
  }, [data, selectedVendor, searchTerm]);

  const pendingCount = useMemo(() => filteredData.length, [filteredData]);

  const [totalByTransfer, totalByCash] = useMemo(() => {
    let transfer = 0;
    let cash = 0;

    filteredData.forEach(payment => {
      const amount = Number(payment.amount);
      if (payment.method?.toUpperCase() === 'TRANSFERENCIA') {
        transfer += amount;
      } else {
        cash += amount;
      }
    });

    return [transfer, cash];
  }, [filteredData]);

  const confirmPayment = async (paymentId: number) => {
    const payment = data.find(p => p.id === paymentId);
    if (!payment) return;

    setSelectedPayment(payment);
    setSelectedBox(null);
    setShowBoxSelector(true);
  };

  const executeConfirmPayment = async () => {
    if (!selectedPayment || selectedBox === null) return;

    setActionInProgress(prev => ({ ...prev, [selectedPayment.id]: true }));

    try {
      await axiosInstance.post(`/orders/pay-requests/${selectedPayment.id}/confirm`, {
        boxId: selectedBox,
        reviewNotes: 'Confirmado desde admin'
      });
      setShowBoxSelector(false);
      setSelectedPayment(null);
      setSelectedBox(null);
      toast.success(`Pago confirmado para orden #${selectedPayment.sell_order?.id}`);
      await fetchData();
    } catch (error: any) {
      console.error('Error confirming payment:', error.response?.data || error);
      toast.error('Error al confirmar el pago');
    } finally {
      setActionInProgress(prev => ({ ...prev, [selectedPayment.id]: false }));
    }
  };

  const rejectPayment = async (paymentId: number) => {
    const payment = data.find(p => p.id === paymentId);
    if (!payment) return;

    setSelectedPayment(payment);
    setRejectReason('');
    setShowRejectModal(true);
  };

  const executeRejectPayment = async () => {
    if (!selectedPayment || !rejectReason.trim()) return;

    setActionInProgress(prev => ({ ...prev, [selectedPayment.id]: true }));

    try {
      await axiosInstance.post(`/orders/pay-requests/${selectedPayment.id}/reject`, {
        reviewNotes: rejectReason
      });
      await fetchData();
      setShowRejectModal(false);
      setSelectedPayment(null);
      setRejectReason('');
      toast.success(`Pago rechazado para orden #${selectedPayment.sell_order?.id}`);
    } catch (error: any) {
      console.error('Error rejecting payment:', error.response?.data || error);
      toast.error('Error al rechazar el pago');
    } finally {
      setActionInProgress(prev => ({ ...prev, [selectedPayment.id]: false }));
    }
  };

  return (
    <Container>
      <Header>
        <h2>
          Solicitudes de Pagos
          {pendingCount > 0 && (
            <span className="pending-count">{pendingCount} pendientes</span>
          )}
        </h2>
        {pendingCount > 0 && (
          <div className='total-by-method'>
            <span>{getPaymentIcon('EFECTIVO')} {formatMoney(totalByCash)}</span>
            <span>{getPaymentIcon('TRANSFERENCIA')} {formatMoney(totalByTransfer)}</span>
          </div>
        )}
      </Header>

      <StyledComponents.SearchBar>
        <div className="search-group">
          <label>
            <select
              value={selectedVendor}
              onChange={(e) => setSelectedVendor(e.target.value)}
            >
              {getVendorOptions(vendorsArray)}
            </select>
          </label>
        </div>
        <div className="search-group">
          <label>
            <StyledComponents.SearchInput
              type="text"
              placeholder="Buscar por orden, cliente o documento..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </label>
        </div>

      </StyledComponents.SearchBar>

      <StyledComponents.PaymentsContainer>
        <StyledComponents.PaymentList>
          {loading ? (
            <div style={{
              textAlign: 'center',
              padding: '2rem',
              color: '#6c757d',
              backgroundColor: 'white',
              borderRadius: '0.75rem',
              border: '2px solid #dee2e6'
            }}>
              Cargando solicitudes...
            </div>
          ) : filteredData.length === 0 ? (
            <StyledComponents.EmptyState>
              No hay solicitudes pendientes que coincidan con los filtros
            </StyledComponents.EmptyState>
          ) : (
            <>
              {filteredData.map((payment) => (
                <StyledComponents.PaymentCard key={payment.id}>
                  <div className="payment-header">
                    <div className="left-info">
                      <div className="order-id">
                        Orden #{payment.sell_order?.id}
                        {payment.sell_order?.futureDocument && payment.sell_order.futureDocument !== 'PROFORMA' && payment.sell_order.document && (
                          <span style={{
                            backgroundColor: '#fff3cd',
                            color: '#856404',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '0.25rem',
                            fontSize: '0.75rem',
                            marginLeft: '0.5rem',
                            fontWeight: 'bold'
                          }}>
                            {payment.sell_order.document}
                          </span>
                        )}
                      </div>
                      <div className="customer-name">{payment.sell_order?.customer?.name || 'Cliente no especificado'}</div>
                      <div className="meta-info">{payment.sell_order?.user?.username} • {formatDate(payment.date)}</div>
                    </div>
                    <div className="amount-info">
                      <div className="amount">{formatMoney(Number(payment.amount))}</div>
                      <div className="method">
                        {getPaymentIcon(payment.method)} {payment.method?.toUpperCase() || 'SIN MÉTODO'}
                      </div>
                    </div>
                  </div>

                  <div className="payment-details">
                    {payment.notes && (
                      <div className="notes-section">
                        <strong>Notas del vendedor:</strong><br />
                        {payment.notes}
                      </div>
                    )}
                  </div>

                  <div className="actions">
                    <button
                      className="confirm-btn"
                      onClick={() => confirmPayment(payment.id)}
                      disabled={actionInProgress[payment.id]}
                    >
                      {actionInProgress[payment.id] ? 'Confirmando...' : '✅ Confirmar'}
                    </button>
                    <button
                      className="reject-btn"
                      onClick={() => rejectPayment(payment.id)}
                      disabled={actionInProgress[payment.id]}
                    >
                      {actionInProgress[payment.id] ? 'Rechazando...' : '❌ Rechazar'}
                    </button>
                  </div>
                </StyledComponents.PaymentCard>
              ))}
            </>
          )}
        </StyledComponents.PaymentList>
      </StyledComponents.PaymentsContainer>

      {/* Modal de selección de caja */}
      {showBoxSelector && selectedPayment && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '0.75rem',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h3 style={{ marginBottom: '1.5rem' }}>
              Seleccionar caja para el pago
            </h3>

            <div style={{ marginBottom: '1rem' }}>
              {selectedPayment.sell_order?.futureDocument && selectedPayment.sell_order.futureDocument !== 'PROFORMA' && (
                <div style={{
                  backgroundColor: '#fff3cd',
                  padding: '0.15rem 0',
                  borderRadius: '0.25rem'
                }}>
                  <strong>Factura:</strong> {selectedPayment.sell_order?.document}
                </div>
              )}
              <strong>Orden:</strong> #{selectedPayment.sell_order?.id}<br />
              <strong>Cliente:</strong> {selectedPayment.sell_order?.customer?.name}<br />
              <strong>Monto:</strong> {formatMoney(Number(selectedPayment.amount))}<br />
              <strong>Método:</strong> {getPaymentIcon(selectedPayment.method)} {selectedPayment.method?.toUpperCase() || 'SIN MÉTODO'}
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="boxSelect" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Selecciona la caja:
              </label>
              <select
                id="boxSelect"
                value={selectedBox || ''}
                onChange={(e) => setSelectedBox(Number(e.target.value))}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #dee2e6',
                  borderRadius: '0.375rem',
                  fontSize: '1rem'
                }}
              >
                <option value="">-- Selecciona una caja --</option>
                {getFilteredBoxes(boxes, selectedPayment.method, selectedPayment.sell_order?.futureDocument).map(box => (
                  <option key={box.id} value={box.id}>
                    {box.name} ({box.isCci ? 'Banco' : 'Efectivo'})
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowBoxSelector(false);
                  setSelectedPayment(null);
                  setSelectedBox(null);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '1px solid #6c757d',
                  backgroundColor: 'white',
                  color: '#6c757d',
                  borderRadius: '0.375rem',
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={executeConfirmPayment}
                disabled={selectedBox === null || actionInProgress[selectedPayment.id]}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  backgroundColor: selectedBox === null ? '#6c757d' : '#28a745',
                  color: 'white',
                  borderRadius: '0.375rem',
                  cursor: selectedBox === null ? 'not-allowed' : 'pointer'
                }}
              >
                {actionInProgress[selectedPayment.id] ? 'Confirmando...' : 'Confirmar Pago'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de rechazo de pago */}
      {showRejectModal && selectedPayment && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '0.75rem',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h3 style={{ marginBottom: '1.5rem', color: '#dc3545' }}>
              Rechazar pago
            </h3>

            <div style={{ marginBottom: '1.5rem' }}>
              <strong>Orden:</strong> #{selectedPayment.sell_order?.id}<br />
              <strong>Cliente:</strong> {selectedPayment.sell_order?.customer?.name}<br />
              <strong>Monto:</strong> {formatMoney(Number(selectedPayment.amount))}<br />
              <strong>Método:</strong> {getPaymentIcon(selectedPayment.method)} {selectedPayment.method?.toUpperCase() || 'SIN MÉTODO'}
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="rejectReason" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Motivo del rechazo: *
              </label>
              <textarea
                id="rejectReason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Indica el motivo por el cual se rechaza este pago..."
                rows={4}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #dee2e6',
                  borderRadius: '0.375rem',
                  fontSize: '1rem',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setSelectedPayment(null);
                  setRejectReason('');
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '1px solid #6c757d',
                  backgroundColor: 'white',
                  color: '#6c757d',
                  borderRadius: '0.375rem',
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={executeRejectPayment}
                disabled={!rejectReason.trim() || actionInProgress[selectedPayment.id]}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  backgroundColor: !rejectReason.trim() ? '#6c757d' : '#dc3545',
                  color: 'white',
                  borderRadius: '0.375rem',
                  cursor: !rejectReason.trim() ? 'not-allowed' : 'pointer'
                }}
              >
                {actionInProgress[selectedPayment.id] ? 'Rechazando...' : 'Rechazar Pago'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
};

export default PendingPaymentsPage;
